import React, { useEffect, useState } from 'react';
import axios from 'axios';
import ListSearch, { filterBySearch, USER_SEARCH_KEYS } from './ListSearch';
import { ModuleShell } from './module-ui';
import { ShieldCheck } from '@phosphor-icons/react';

import { BACKEND_URL, apiPath } from '../config/apiBase';
import { notifySuccess, notifyError } from '../lib/notify';
const API = apiPath('/api/module-access');

function authHeaders() {
  const token = localStorage.getItem('token');
  return { withCredentials: true, headers: { Authorization: `Bearer ${token}` } };
}

function arraysEqual(a, b) {
  if (a.length !== b.length) return false;
  const sb = new Set(b);
  return a.every((k) => sb.has(k));
}

const ROLE_LABELS = {
  super_admin: 'Super Admin',
  admin: 'Admin',
  manager: 'Manager',
  lead: 'Team Lead',
  member: 'Staff / Member',
  agent: 'Agent',
  client: 'Client',
  pending: 'Pending Approval',
  end_user: 'Client',
};

function ModuleAccessPage() {
  const [tab, setTab] = useState('roles'); // 'roles' | 'users'
  const [openGroups, setOpenGroups] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [roles, setRoles] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedRole, setSelectedRole] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedModules, setSelectedModules] = useState([]);
  const [userSearch, setUserSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const load = async ({ keepRole, keepUser } = {}) => {
    setLoading(true);
    try {
      const [catRes, rolesRes, usersRes] = await Promise.all([
        axios.get(`${API}/catalog`, authHeaders()),
        axios.get(`${API}/roles`, authHeaders()),
        axios.get(`${API}/users`, authHeaders()),
      ]);
      const cat = catRes.data.modules || [];
      const rolesData = rolesRes.data || [];
      const usersData = usersRes.data || [];
      setCatalog(cat);
      setRoles(rolesData);
      setUsers(usersData);

      const roleTarget = rolesData.find((r) => r.role === (keepRole || selectedRole?.role))
        || rolesData.find((r) => r.editable)
        || rolesData[0];
      if (roleTarget) {
        setSelectedRole(roleTarget);
        if (tab === 'roles') setSelectedModules([...(roleTarget.allowed_modules || [])]);
      }

      const userTarget = usersData.find((u) => u.user_id === (keepUser || selectedUser?.user_id)) || null;
      setSelectedUser(userTarget);
      if (tab === 'users' && userTarget) {
        setSelectedModules([...(userTarget.allowed_modules || [])]);
      }

      setError(null);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load module access');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const switchTab = (next) => {
    setTab(next);
        setError(null);
    if (next === 'roles' && selectedRole) {
      setSelectedModules([...(selectedRole.allowed_modules || [])]);
    } else if (next === 'users' && selectedUser) {
      setSelectedModules([...(selectedUser.allowed_modules || [])]);
    } else {
      setSelectedModules([]);
    }
  };

  const selectRole = (r) => {
    setSelectedRole(r);
    setSelectedModules([...(r.allowed_modules || [])]);
        setError(null);
  };

  const selectUser = (u) => {
    setSelectedUser(u);
    setSelectedModules([...(u.allowed_modules || [])]);
        setError(null);
  };

  const toggleModule = (key) => {
    if (tab === 'roles' && !selectedRole?.editable) return;
    if (tab === 'users') {
      const role = selectedUser?.role;
      if (role === 'super_admin' || role === 'pending') return;
    }
    setSelectedModules((prev) => (
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    ));
  };

  const saveRole = async () => {
    if (!selectedRole || !selectedRole.editable) return;
    setSaving(true);
        setError(null);
    try {
      await axios.put(`${API}/roles/${selectedRole.role}`, { allowed_modules: selectedModules }, authHeaders());
      notifySuccess(`Saved module access for ${selectedRole.label}`);
      await load({ keepRole: selectedRole.role });
    } catch (e) {
      setError(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const resetRole = async () => {
    if (!selectedRole || !selectedRole.editable) return;
    if (!window.confirm(`Reset ${selectedRole.label} to default modules?`)) return;
    setSaving(true);
        setError(null);
    try {
      await axios.delete(`${API}/roles/${selectedRole.role}`, authHeaders());
      notifySuccess(`${selectedRole.label} reset to defaults`);
      await load({ keepRole: selectedRole.role });
    } catch (e) {
      setError(e.response?.data?.detail || 'Reset failed');
    } finally {
      setSaving(false);
    }
  };

  const saveUser = async () => {
    if (!selectedUser) return;
    if (selectedUser.role === 'super_admin' || selectedUser.role === 'pending') return;
    setSaving(true);
        setError(null);
    try {
      await axios.patch(`${API}/users/${selectedUser.user_id}`, { allowed_modules: selectedModules }, authHeaders());
      notifySuccess(`Saved custom modules for ${selectedUser.name}`);
      await load({ keepUser: selectedUser.user_id });
    } catch (e) {
      setError(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const resetUser = async () => {
    if (!selectedUser) return;
    if (!window.confirm(`Reset ${selectedUser.name} to their role defaults?`)) return;
    setSaving(true);
        setError(null);
    try {
      await axios.delete(`${API}/users/${selectedUser.user_id}/custom`, authHeaders());
      notifySuccess(`${selectedUser.name} reset to role defaults`);
      await load({ keepUser: selectedUser.user_id });
    } catch (e) {
      setError(e.response?.data?.detail || 'Reset failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p style={{ color: '#6B7280' }}>Loading…</p>;

  const filteredUsers = filterBySearch(users, userSearch, [...USER_SEARCH_KEYS, 'role']);
  const roleDirty = selectedRole && !arraysEqual(selectedModules, selectedRole.allowed_modules || []);
  const userDirty = selectedUser && !arraysEqual(selectedModules, selectedUser.allowed_modules || []);
  const userLocked = selectedUser && (selectedUser.role === 'super_admin' || selectedUser.role === 'pending');
  const roleModulesForUser = selectedUser
    ? (roles.find((r) => r.role === selectedUser.role)?.allowed_modules || [])
    : [];

  // Catalog entries carrying a `parent` are the screens of a module that is
  // granted screen by screen (Supporting Tools). They are pulled out of the
  // top-level grid and listed under their parent instead, so the editor does
  // not become one flat wall of checkboxes.
  const topLevel = catalog.filter((m) => !m.parent);
  const childrenOf = (key) => catalog.filter((m) => m.parent === key);

  // The screens under a "<module> — screens" heading stay folded until that heading
  // is clicked, so the page is not a wall of checkboxes.
  const toggleGroup = (key) => setOpenGroups((prev) => (
    prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
  ));

  const moduleCheckbox = (mod, editable) => {
    const checked = selectedModules.includes(mod.key);
    const fromRole = tab === 'users' && roleModulesForUser.includes(mod.key);
    const userExtra = tab === 'users' && checked && !fromRole;
    return (
      <label
        key={mod.key}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '10px',
          border: `1px solid ${userExtra ? '#A7F3D0' : '#E5E7EB'}`,
          borderRadius: '8px',
          cursor: editable ? 'pointer' : 'not-allowed',
          opacity: editable ? 1 : 0.7,
          background: checked ? (userExtra ? '#ECFDF5' : '#EFF6FF') : 'white',
        }}
      >
        <input
          type="checkbox"
          checked={checked}
          disabled={!editable}
          onChange={() => toggleModule(mod.key)}
        />
        <span style={{ fontSize: '12px', flex: 1 }}>{mod.label}</span>
        {userExtra && <span style={{ fontSize: '10px', color: '#059669', fontWeight: 600 }}>USER</span>}
      </label>
    );
  };

  // Every screen of one module, with a select-all/none shortcut — granting 30
  // screens one checkbox at a time is not a reasonable ask.
  const screenGroup = (parent, editable) => {
    const kids = childrenOf(parent.key);
    if (kids.length === 0) return null;
    const keys = kids.map((k) => k.key);
    const allOn = keys.every((k) => selectedModules.includes(k));
    const someOn = !allOn && keys.some((k) => selectedModules.includes(k));
    const toggleAll = () => setSelectedModules((prev) => (allOn
      ? prev.filter((k) => !keys.includes(k))
      : [...new Set([...prev, ...keys])]));
    const isOpen = openGroups.includes(parent.key);
    return (
      <div key={`group-${parent.key}`} style={{ border: '1px solid #E5E7EB', borderRadius: '8px', padding: '12px', marginBottom: '8px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: isOpen ? '8px' : 0 }}>
          <div
            onClick={() => toggleGroup(parent.key)}
            role="button"
            tabIndex={0}
            aria-expanded={isOpen}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleGroup(parent.key); } }}
            style={{ cursor: 'pointer', flex: 1 }}
            data-testid={`module-group-toggle-${parent.key}`}
          >
            <div style={{ fontSize: '12px', fontWeight: 700 }}>{parent.label} — screens</div>
            <div style={{ fontSize: '11px', color: '#6B7280' }}>
              {keys.filter((k) => selectedModules.includes(k)).length} of {keys.length} granted
              {someOn ? ' (partial)' : ''}
            </div>
          </div>
          {editable && isOpen && (
            <button
              type="button"
              onClick={toggleAll}
              style={{ padding: '4px 10px', fontSize: '11px', background: 'white', border: '1px solid #D1D5DB', borderRadius: '6px', cursor: 'pointer' }}
            >
              {allOn ? 'Clear all' : 'Select all'}
            </button>
          )}
        </div>
        {isOpen && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '8px' }}>
            {kids.map((k) => moduleCheckbox(k, editable))}
          </div>
        )}
      </div>
    );
  };

  const moduleGrid = (editable) => (
    <>
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '8px', marginBottom: '8px' }}>
      {topLevel.map((mod) => {
        const checked = selectedModules.includes(mod.key);
        const fromRole = tab === 'users' && roleModulesForUser.includes(mod.key);
        const userExtra = tab === 'users' && checked && !fromRole;
        return (
          <label
            key={mod.key}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px',
              border: `1px solid ${userExtra ? '#A7F3D0' : '#E5E7EB'}`,
              borderRadius: '8px',
              cursor: editable ? 'pointer' : 'not-allowed',
              opacity: editable ? 1 : 0.7,
              background: checked ? (userExtra ? '#ECFDF5' : '#EFF6FF') : 'white',
            }}
          >
            <input
              type="checkbox"
              checked={checked}
              disabled={!editable}
              onChange={() => toggleModule(mod.key)}
            />
            <span style={{ fontSize: '12px', flex: 1 }}>{mod.label}</span>
            {userExtra && <span style={{ fontSize: '10px', color: '#059669', fontWeight: 600 }}>USER</span>}
          </label>
        );
      })}
    </div>
    {topLevel.map((mod) => screenGroup(mod, editable))}
    </>
  );

  return (
    <ModuleShell
      title="Module Access"
      subtitle="Super admin only — set modules by role, or override for a specific user. User-level access takes priority over the role."
      icon={ShieldCheck}
      subnav={(
        <nav className="tk-subnav" aria-label="Module access navigation">
          <button
            type="button"
            className={`tk-subnav-link${tab === 'roles' ? ' is-active' : ''}`}
            onClick={() => switchTab('roles')}
          >
            <span>By Role</span>
          </button>
          <button
            type="button"
            className={`tk-subnav-link${tab === 'users' ? ' is-active' : ''}`}
            onClick={() => switchTab('users')}
          >
            <span>By User</span>
          </button>
        </nav>
      )}
    >
      {error && <div style={{ padding: '12px', background: '#FEE2E2', color: '#B91C1C', borderRadius: '8px', marginBottom: '8px' }}>{error}</div>}

      {tab === 'roles' && (
        <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr', gap: '24px' }}>
          <div style={{ border: '1px solid #E5E7EB', borderRadius: '8px', overflow: 'hidden', alignSelf: 'start' }}>
            <div style={{ padding: '12px', background: '#F9FAFB', fontWeight: '600', fontSize: '12px' }}>Roles</div>
            <div>
              {roles.map((r) => (
                <button
                  key={r.role}
                  type="button"
                  onClick={() => selectRole(r)}
                  style={{
                    display: 'block',
                    width: '100%',
                    textAlign: 'left',
                    padding: '6px 10px',
                    border: 'none',
                    borderBottom: '1px solid #F3F4F6',
                    background: selectedRole?.role === r.role ? '#EFF6FF' : 'white',
                    cursor: 'pointer',
                  }}
                >
                  <div style={{ fontWeight: '500', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    {r.label}
                    {!r.editable && <span style={{ fontSize: '11px', color: '#9CA3AF' }}>(locked)</span>}
                  </div>
                  <div style={{ fontSize: '12px', color: '#6B7280' }}>
                    {(r.allowed_modules || []).length} modules · {r.customized ? 'Customized' : 'Default'}
                  </div>
                </button>
              ))}
            </div>
          </div>

          <div>
            {selectedRole ? (
              <>
                <h2 style={{ fontSize: '12px', fontWeight: '600', marginBottom: '4px' }}>{selectedRole.label}</h2>
                <p style={{ color: '#6B7280', fontSize: '12px', marginBottom: '8px' }}>
                  {selectedRole.editable
                    ? `Default modules for everyone with the ${selectedRole.label} role. Individual users can still be given extra modules on the By User tab.`
                    : selectedRole.role === 'super_admin'
                      ? 'Super Admin always has access to every module (locked).'
                      : 'Pending users have no module access until approved (locked).'}
                </p>
                {moduleGrid(!!selectedRole.editable)}
                {selectedRole.editable && (
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <button type="button" onClick={saveRole} disabled={saving || !roleDirty} style={{ padding: '6px 12px', background: roleDirty ? '#002FA7' : '#9CA3AF', color: 'white', border: 'none', borderRadius: '8px', fontWeight: '600', cursor: roleDirty && !saving ? 'pointer' : 'not-allowed' }}>{saving ? 'Saving…' : 'Save'}</button>
                    <button type="button" onClick={resetRole} disabled={saving} style={{ padding: '6px 12px', background: 'white', border: '1px solid #D1D5DB', borderRadius: '8px', cursor: 'pointer' }}>Reset to defaults</button>
                    {roleDirty && <span style={{ fontSize: '12px', color: '#B45309' }}>Unsaved changes</span>}
                  </div>
                )}
              </>
            ) : (
              <p style={{ color: '#6B7280' }}>Select a role to edit its module access.</p>
            )}
          </div>
        </div>
      )}

      {tab === 'users' && (
        <div style={{ display: 'grid', gridTemplateColumns: '320px 1fr', gap: '24px' }}>
          <div style={{ border: '1px solid #E5E7EB', borderRadius: '8px', overflow: 'hidden', alignSelf: 'start' }}>
            <div style={{ padding: '12px', background: '#F9FAFB', fontWeight: '600', fontSize: '12px' }}>Users</div>
            <div style={{ padding: '12px', borderBottom: '1px solid #E5E7EB' }}>
              <ListSearch
                value={userSearch}
                onChange={setUserSearch}
                placeholder="Search users by name, email, role…"
                resultCount={filteredUsers.length}
                totalCount={users.length}
                maxWidth="100%"
                style={{ marginBottom: 0 }}
              />
            </div>
            <div style={{ maxHeight: '520px', overflowY: 'auto' }}>
              {filteredUsers.length === 0 && (
                <p style={{ padding: '10px', color: '#6B7280', fontSize: '12px' }}>No users match your search.</p>
              )}
              {filteredUsers.map((u) => (
                <button
                  key={u.user_id}
                  type="button"
                  onClick={() => selectUser(u)}
                  style={{
                    display: 'block',
                    width: '100%',
                    textAlign: 'left',
                    padding: '6px 10px',
                    border: 'none',
                    borderBottom: '1px solid #F3F4F6',
                    background: selectedUser?.user_id === u.user_id ? '#EFF6FF' : 'white',
                    cursor: 'pointer',
                  }}
                >
                  <div style={{ fontWeight: '500', fontSize: '12px' }}>{u.name}</div>
                  <div style={{ fontSize: '12px', color: '#6B7280' }}>
                    {ROLE_LABELS[u.role] || u.role} · {u.custom_modules ? 'Custom override' : 'Role default'}
                  </div>
                  <div style={{ fontSize: '11px', color: '#9CA3AF', marginTop: '2px' }}>{u.email}</div>
                </button>
              ))}
            </div>
          </div>

          <div>
            {selectedUser ? (
              <>
                <h2 style={{ fontSize: '12px', fontWeight: '600', marginBottom: '4px' }}>{selectedUser.name}</h2>
                <p style={{ color: '#6B7280', fontSize: '12px', marginBottom: '8px' }}>
                  {selectedUser.email} — {ROLE_LABELS[selectedUser.role] || selectedUser.role}
                </p>
                {userLocked ? (
                  <p style={{ color: '#6B7280', fontSize: '12px', marginBottom: '8px' }}>
                    {selectedUser.role === 'super_admin'
                      ? 'Super Admin always has every module — custom overrides are not allowed.'
                      : 'Pending users have no module access until approved.'}
                  </p>
                ) : (
                  <p style={{ color: '#6B7280', fontSize: '12px', marginBottom: '8px' }}>
                    {selectedUser.custom_modules
                      ? 'This user has a custom module set that overrides their role. Modules marked USER are beyond the role default.'
                      : 'Currently using role defaults. Tick extra modules (or change the set) and Save to create a user-level override.'}
                  </p>
                )}
                {moduleGrid(!userLocked)}
                {!userLocked && (
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <button type="button" onClick={saveUser} disabled={saving || !userDirty} style={{ padding: '6px 12px', background: userDirty ? '#002FA7' : '#9CA3AF', color: 'white', border: 'none', borderRadius: '8px', fontWeight: '600', cursor: userDirty && !saving ? 'pointer' : 'not-allowed' }}>{saving ? 'Saving…' : 'Save user override'}</button>
                    {selectedUser.custom_modules && (
                      <button type="button" onClick={resetUser} disabled={saving} style={{ padding: '6px 12px', background: 'white', border: '1px solid #D1D5DB', borderRadius: '8px', cursor: 'pointer' }}>Reset to role defaults</button>
                    )}
                    {userDirty && <span style={{ fontSize: '12px', color: '#B45309' }}>Unsaved changes</span>}
                  </div>
                )}
              </>
            ) : (
              <p style={{ color: '#6B7280' }}>Search and select a user to give them specific module access.</p>
            )}
          </div>
        </div>
      )}
    </ModuleShell>
  );
}

export default ModuleAccessPage;
